import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { CallAPIDeletePromise, CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";
import api from "Common/api";
import { getAuthHeader } from "helpers/jwt-token-access/auth-token-header";
import { getUserType } from "slices/userProfile/thunk";
import { CalendarDays, ClipboardCheck, FileSignature, FileText, Link2, MapPin, Plus, RefreshCw, Search, Unlink, UsersRound } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { ContractLoadingModal } from "components/contractLoadingModal";
import { toast } from "sonner";
import tradesmenLogo from "assets/images/tradesmen-logo-dark.png";

type HicSubcontractAgreementsProps = {
  project: any;
  onAgreementsChange?: (agreements: any[]) => void;
};

function statusLabel(status: string) {
  switch (status) {
    case "fully_executed": return "Fully Signed";
    case "contractor_signed": return "Subcontractor Signature Pending";
    case "subcontractor_signed": return "Contractor Signature Pending";
    case "sent": return "Sent for Signature";
    case "draft": return "Draft";
    default: return status || "Draft";
  }
}

function statusVariant(status: string) {
  if (status === "fully_executed") return "success";
  if (status === "draft") return "secondary";
  if (status === "sent") return "info";
  return "warning";
}

function formatMoney(value: any) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatDate(value: any) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getSubcontractorDisplay(agreement: any) {
  const subcontractor = agreement?.payload?.subcontractor || {};
  return {
    name: subcontractor.company || subcontractor.name || "Subcontractor not selected",
    email: subcontractor.email || agreement.subcontractorEmail || "Email not available",
  };
}

function getResponseData(response: any) {
  return response?.data?.data || response?.data || response || [];
}

const SubcontractorAvatar: React.FC<{ agreement: any; name: string }> = ({ agreement, name }) => {
  const sources = useMemo(() => Array.from(new Set([
    agreement?.subcontractorCompanyLogoUrl,
    agreement?.subcontractorProfilePhotoUrl,
    tradesmenLogo,
  ].filter(Boolean))), [agreement?.subcontractorCompanyLogoUrl, agreement?.subcontractorProfilePhotoUrl]);
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => setSourceIndex(0), [sources.join("|")]);

  const initials = String(name || "Subcontractor")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <div className="hic-subcontractor-avatar" aria-label={`${name} identity`}>
      {sources[sourceIndex] ? (
        <img
          src={sources[sourceIndex]}
          alt={`${name} logo`}
          onError={() => setSourceIndex((index) => index + 1)}
        />
      ) : (
        <span>{initials || "SC"}</span>
      )}
      <span className="hic-subcontractor-role-mark" aria-hidden="true">SC</span>
    </div>
  );
};

const HicSubcontractAgreements: React.FC<HicSubcontractAgreementsProps> = ({ project, onAgreementsChange }) => {
  const navigate = useNavigate();
  const projectId = project?.p_id;
  const userType = getUserType();
  const canManage = userType === "contractor" || userType === "testContractor";
  const [agreements, setAgreements] = useState<any[]>([]);
  const [attachable, setAttachable] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [attachLoading, setAttachLoading] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [showAttach, setShowAttach] = useState(false);
  const [attachSearch, setAttachSearch] = useState("");
  const [signAgreement, setSignAgreement] = useState<any>(null);
  const [signing, setSigning] = useState(false);
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const [error, setError] = useState("");

  const primaryAgreements = useMemo(
    () => agreements.filter((agreement) => !agreement?.isChangeOrder && !agreement?.parentAgreementId),
    [agreements],
  );

  const changeOrdersByAgreement = useMemo(() => {
    return agreements.reduce((grouped: Record<string, any[]>, agreement: any) => {
      if (!agreement?.parentAgreementId) return grouped;
      const parentId = String(agreement.parentAgreementId);
      grouped[parentId] = [...(grouped[parentId] || []), agreement];
      return grouped;
    }, {});
  }, [agreements]);

  const filteredAttachable = useMemo(() => {
    const query = attachSearch.trim().toLowerCase();
    if (!query) return attachable;
    return attachable.filter((agreement) => [
      agreement.title,
      agreement.projectName,
      agreement.projectSite,
      agreement.subcontractorEmail,
      agreement.id,
      statusLabel(agreement.status),
    ].some((value) => String(value || "").toLowerCase().includes(query)));
  }, [attachSearch, attachable]);

  const loadAgreements = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const response: any = await CallAPIGetPromise(`/contract/subcontract-agreements/hic/${projectId}`);
      const next = getResponseData(response);
      const list = Array.isArray(next) ? next : [];
      setAgreements(list);
      onAgreementsChange?.(list);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || "Unable to load connected subcontract agreements.");
    } finally {
      setLoading(false);
    }
  }, [projectId, onAgreementsChange]);

  useEffect(() => {
    loadAgreements();
  }, [loadAgreements]);

  const openAttachModal = async () => {
    setShowAttach(true);
    setAttachSearch("");
    setAttachLoading(true);
    try {
      const response: any = await CallAPIGetPromise(`/contract/subcontract-agreements/hic/${projectId}/attachable/list`);
      const next = getResponseData(response);
      setAttachable(Array.isArray(next) ? next : []);
    } catch (requestError: any) {
      toast.error(requestError?.response?.data?.message || "Unable to load existing agreements.");
    } finally {
      setAttachLoading(false);
    }
  };

  const attachAgreement = async (agreementId: number) => {
    setActionId(agreementId);
    try {
      await CallAPIPostPromise(`/contract/subcontract-agreements/hic/${projectId}/attach/${agreementId}`, {});
      toast.success("Subcontract agreement connected to this HIC.");
      setAttachable((current) => current.filter((item) => item.id !== agreementId));
      await loadAgreements();
      setShowAttach(false);
    } catch (requestError: any) {
      toast.error(requestError?.response?.data?.message || "Unable to attach this agreement.");
    } finally {
      setActionId(null);
    }
  };

  const detachAgreement = async (agreementId: number) => {
    setActionId(agreementId);
    try {
      await CallAPIDeletePromise(`/contract/subcontract-agreements/hic/${projectId}/attach/${agreementId}`);
      toast.success("Draft agreement detached from this HIC.");
      await loadAgreements();
    } catch (requestError: any) {
      toast.error(requestError?.response?.data?.message || "Unable to detach this agreement.");
    } finally {
      setActionId(null);
    }
  };

  const openSignModal = (agreement: any) => {
    if (!agreement?.canSign) {
      toast.info("This agreement has already been signed by you.");
      return;
    }
    setSignAgreement(agreement);
  };

  const submitAgreementSignature = async () => {
    if (!signAgreement?.id || !signatureRef.current || signatureRef.current.isEmpty()) {
      toast.warning("Please draw your signature before submitting.");
      return;
    }
    const headers = getAuthHeader();
    if (!headers) return;

    setSigning(true);
    try {
      const signatureDataUrl = signatureRef.current.toDataURL("image/png");
      const signatureBlob = await fetch(signatureDataUrl).then((response) => response.blob());
      const body = new FormData();
      body.append("file", signatureBlob, "signature.png");
      const response: any = await api.post(`/contract/subcontract-agreements/${signAgreement.id}/sign`, body, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const signedAgreement = response?.data || response;
      const creatorIsSigning = signAgreement.viewerRole === signAgreement.creatorRole;
      const hasGuestParty = !signAgreement.contractorUserId || !signAgreement.subcontractorUserId;

      if (creatorIsSigning && hasGuestParty && signedAgreement?.status !== "fully_executed") {
        try {
          await CallAPIPostPromise(`/contract/subcontract-agreements/${signAgreement.id}/generate-pdf`, { sendGuestInvite: true });
        } catch (inviteError: any) {
          toast.warning(inviteError?.response?.data?.message || "Signed successfully, but the guest signing email could not be sent.");
        }
      }

      signatureRef.current.clear();
      setSignAgreement(null);
      toast.success(response?.message || "Subcontract agreement signed successfully.");
      await loadAgreements();
    } catch (requestError: any) {
      toast.error(requestError?.response?.data?.message || "Unable to sign this subcontract agreement.");
    } finally {
      setSigning(false);
    }
  };

  if (!projectId) {
    return <Alert variant="info">Save the homeowner project before adding a subcontract agreement.</Alert>;
  }

  return (
    <div className="tailwind-prefix space-y-4">
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <UsersRound className="h-5 w-5 text-blue-600" /> Subcontract Agreements
            </div>
            <p className="mt-1 mb-0 text-sm text-gray-600 max-w-3xl">
              Create a new agreement with this HIC's homeowner and job-site details prefilled, or connect an existing independent agreement.
            </p>
          </div>
          {canManage ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="primary"
                onClick={() => navigate(`/create-contract?type=subcontract&hicProjectId=${projectId}`)}
              >
                <Plus size={16} className="me-1" /> Create New Agreement
              </Button>
              <Button variant="outline-primary" onClick={openAttachModal}>
                <Link2 size={16} className="me-1" /> Attach Existing
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-5 text-center">
          <Spinner size="sm" className="me-2" /> Loading connected agreements...
        </div>
      ) : primaryAgreements.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-gray-400" />
          <div className="mt-3 text-base font-medium text-gray-900">No subcontract agreements connected</div>
          <div className="mt-1 text-sm text-gray-500">Subcontract agreements are optional and can be added now or later.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {primaryAgreements.map((agreement) => {
            const subcontractor = getSubcontractorDisplay(agreement);
            const amount = formatMoney(agreement.contractSum);
            const changeOrders = changeOrdersByAgreement[String(agreement.id)] || [];
            return (
              <div key={agreement.id} className="hic-connected-card">
                <div className="hic-connected-card-layout">
                  <div className="hic-connected-identity">
                    <SubcontractorAvatar agreement={agreement} name={subcontractor.name} />
                    <div className="hic-connected-copy">
                      <div className="hic-connected-eyebrow">Subcontractor</div>
                      <div className="hic-connected-name" title={subcontractor.name}>{subcontractor.name}</div>
                      <div className="hic-connected-email" title={subcontractor.email}>{subcontractor.email}</div>
                      <div className="hic-connected-agreement-row">
                        <span className="hic-connected-agreement-title">{agreement.title || agreement.projectName || "Subcontract Agreement"}</span>
                        <Badge className="hic-connected-status" bg={statusVariant(agreement.status)}>{statusLabel(agreement.status)}</Badge>
                      </div>
                      <div className="hic-connected-meta">
                        <span>Agreement #{agreement.id}</span>
                        {amount ? <span>{amount}</span> : null}
                        <span className="hic-connected-address"><MapPin size={13} /> {agreement.projectSite || project.p_street_address || "Address pending"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="hic-connected-actions">
                    {agreement.status === "draft" && canManage ? (
                      <Button
                        size="sm"
                        onClick={() => navigate(`/create-contract?type=subcontract&id=${agreement.id}&hicProjectId=${projectId}`)}
                      >
                        Continue Editing
                      </Button>
                    ) : null}
                    {agreement.canSign ? (
                      <Button size="sm" variant="primary" onClick={() => openSignModal(agreement)}>
                        <FileSignature size={15} className="me-1" /> Review &amp; Sign
                      </Button>
                    ) : null}
                    {agreement.contractUrl ? (
                      <Button size="sm" variant="outline-primary" onClick={() => window.open(agreement.contractUrl, "_blank")}>
                        View PDF
                      </Button>
                    ) : null}
                    {agreement.status === "fully_executed" && canManage ? (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => navigate(`/create-contract?type=subcontract&changeOrderFor=${agreement.id}`)}
                      >
                        <Plus size={14} className="me-1" /> Change Order
                      </Button>
                    ) : null}
                    {agreement.status === "draft" && canManage ? (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={actionId === agreement.id}
                        onClick={() => detachAgreement(agreement.id)}
                      >
                        {actionId === agreement.id ? <Spinner size="sm" /> : <><Unlink size={14} className="me-1" /> Detach</>}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {changeOrders.length > 0 ? (
                  <div className="hic-change-orders">
                    <div className="hic-change-orders-heading">
                      <span><ClipboardCheck size={15} /> Change Order History</span>
                      <Badge bg="secondary">{changeOrders.length}</Badge>
                    </div>
                    <div className="hic-change-orders-list">
                      {changeOrders.map((changeOrder) => {
                        const changeAmount = formatMoney(changeOrder.contractSum);
                        return (
                          <div key={changeOrder.id} className="hic-change-order-row">
                            <div className="hic-change-order-copy">
                              <div className="hic-change-order-title">
                                {changeOrder.title || "Subcontract Change Order"}
                                <Badge className="hic-connected-status" bg={statusVariant(changeOrder.status)}>
                                  {statusLabel(changeOrder.status)}
                                </Badge>
                              </div>
                              <div className="hic-connected-meta">
                                <span>Change Order #{changeOrder.id}</span>
                                {changeAmount ? <span>{changeAmount}</span> : null}
                              </div>
                            </div>
                            <div className="hic-change-order-actions">
                              {changeOrder.status === "draft" && canManage ? (
                                <Button
                                  size="sm"
                                  onClick={() => navigate(`/create-contract?type=subcontract&id=${changeOrder.id}&hicProjectId=${projectId}`)}
                                >
                                  Continue Editing
                                </Button>
                              ) : null}
                              {changeOrder.canSign ? (
                                <Button size="sm" variant="primary" onClick={() => openSignModal(changeOrder)}>
                                  <FileSignature size={14} className="me-1" /> Review &amp; Sign
                                </Button>
                              ) : null}
                              {changeOrder.contractUrl ? (
                                <Button size="sm" variant="outline-primary" onClick={() => window.open(changeOrder.contractUrl, "_blank")}>
                                  View PDF
                                </Button>
                              ) : null}
                              {!changeOrder.canSign && changeOrder.status !== "draft" ? (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => navigate(`/create-contract?type=subcontract&id=${changeOrder.id}&hicProjectId=${projectId}`)}
                                >
                                  Review
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .hic-connected-card {
          background: #fff; border: 1px solid #dfe6f0; border-radius: 14px; padding: 18px 20px;
          box-shadow: 0 3px 12px rgba(16, 24, 40, .04); transition: border-color .16s ease, box-shadow .16s ease;
        }
        .hic-connected-card:hover { border-color: #b9c9ef; box-shadow: 0 8px 24px rgba(27, 52, 107, .07); }
        .hic-connected-card-layout { align-items: center; display: flex; gap: 24px; justify-content: space-between; }
        .hic-connected-identity { align-items: center; display: flex; gap: 16px; min-width: 0; }
        .hic-subcontractor-avatar {
          align-items: center; background: linear-gradient(145deg, #f4f7ff, #e8efff); border: 1px solid #cfdbf8;
          border-radius: 14px; color: #315ed8; display: inline-flex; flex: 0 0 auto; font-size: 18px; font-weight: 800;
          height: 64px; justify-content: center; padding: 7px; position: relative; width: 64px;
        }
        .hic-subcontractor-avatar img { border-radius: 9px; height: 100%; object-fit: contain; width: 100%; }
        .hic-subcontractor-role-mark {
          align-items: center; background: #315ed8; border: 2px solid #fff; border-radius: 999px; bottom: -5px; color: #fff;
          display: inline-flex; font-size: 8px; font-weight: 800; height: 22px; justify-content: center; letter-spacing: .02em;
          position: absolute; right: -5px; width: 22px;
        }
        .hic-connected-copy { min-width: 0; }
        .hic-connected-eyebrow { color: #667085; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
        .hic-connected-name { color: #182230; font-size: 17px; font-weight: 750; line-height: 1.3; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hic-connected-email { color: #667085; font-size: 12px; margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hic-connected-agreement-row { align-items: center; display: flex; flex-wrap: wrap; gap: 7px; margin-top: 9px; }
        .hic-connected-agreement-title { color: #344054; font-size: 13px; font-weight: 650; }
        .hic-connected-status { border-radius: 999px; font-size: 10px; font-weight: 700; padding: 4px 8px; }
        .hic-connected-meta { align-items: center; color: #7c8798; display: flex; flex-wrap: wrap; font-size: 11px; gap: 5px 14px; margin-top: 6px; }
        .hic-connected-meta > span:not(:first-child)::before { color: #c1c9d6; content: "•"; margin-right: 14px; }
        .hic-connected-address { align-items: center; display: inline-flex; min-width: 0; }
        .hic-connected-address svg { flex: 0 0 auto; margin-right: 4px; }
        .hic-connected-actions { display: flex; flex: 0 0 auto; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }
        .hic-change-orders { border-top: 1px solid #e8edf5; margin-top: 16px; padding-top: 14px; }
        .hic-change-orders-heading { align-items: center; color: #475467; display: flex; font-size: 12px; font-weight: 700; gap: 8px; margin-bottom: 8px; }
        .hic-change-orders-heading > span { align-items: center; display: inline-flex; gap: 6px; }
        .hic-change-orders-list { display: grid; gap: 8px; }
        .hic-change-order-row { align-items: center; background: #f8fafc; border: 1px solid #e8edf5; border-radius: 10px; display: flex; gap: 16px; justify-content: space-between; padding: 11px 12px; }
        .hic-change-order-copy { min-width: 0; }
        .hic-change-order-title { align-items: center; color: #344054; display: flex; flex-wrap: wrap; font-size: 13px; font-weight: 700; gap: 7px; }
        .hic-change-order-actions { display: flex; flex: 0 0 auto; flex-wrap: wrap; gap: 7px; justify-content: flex-end; }
        @media (max-width: 991.98px) {
          .hic-connected-card-layout { align-items: stretch; flex-direction: column; gap: 16px; }
          .hic-connected-actions { justify-content: flex-start; padding-left: 80px; }
          .hic-change-order-row { align-items: stretch; flex-direction: column; }
          .hic-change-order-actions { justify-content: flex-start; }
        }
        @media (max-width: 575.98px) {
          .hic-connected-card { padding: 16px; }
          .hic-connected-identity { align-items: flex-start; }
          .hic-subcontractor-avatar { height: 54px; width: 54px; }
          .hic-connected-actions { display: grid; grid-template-columns: 1fr; padding-left: 0; }
          .hic-connected-actions .btn { width: 100%; }
          .hic-change-order-actions { display: grid; grid-template-columns: 1fr; }
          .hic-change-order-actions .btn { width: 100%; }
          .hic-connected-meta > span:not(:first-child)::before { display: none; }
        }
        .hic-attach-modal {
          border: 0;
          border-radius: 18px;
          box-shadow: 0 24px 80px rgba(15, 23, 42, .24);
          overflow: hidden;
        }
        .hic-attach-modal .modal-header {
          align-items: flex-start;
          background: linear-gradient(135deg, #f7faff 0%, #ffffff 72%);
          border-bottom: 1px solid #e8edf5;
          padding: 24px 28px 20px;
        }
        .hic-attach-title-row { display: flex; align-items: flex-start; gap: 14px; padding-right: 28px; }
        .hic-attach-title-icon {
          align-items: center; background: #e9f0ff; border-radius: 12px; color: #2f5fe3;
          display: inline-flex; flex: 0 0 auto; height: 46px; justify-content: center; width: 46px;
        }
        .hic-attach-modal .modal-title { color: #16213e; font-size: 21px; font-weight: 700; line-height: 1.3; }
        .hic-attach-subtitle { color: #667085; font-size: 14px; line-height: 1.5; margin: 5px 0 0; max-width: 720px; }
        .hic-attach-modal .btn-close { margin-top: 4px; }
        .hic-attach-modal .modal-body { background: #f8fafc; padding: 20px 28px 24px; }
        .hic-attach-toolbar { align-items: center; display: flex; gap: 12px; justify-content: space-between; margin-bottom: 14px; }
        .hic-attach-search { flex: 1 1 auto; max-width: 560px; position: relative; }
        .hic-attach-search svg { color: #98a2b3; left: 14px; position: absolute; top: 50%; transform: translateY(-50%); }
        .hic-attach-search .form-control { border-color: #d9e0ea; border-radius: 10px; height: 44px; padding-left: 42px; }
        .hic-attach-search .form-control:focus { border-color: #7c9cf4; box-shadow: 0 0 0 3px rgba(47, 95, 227, .12); }
        .hic-attach-count { color: #667085; flex: 0 0 auto; font-size: 13px; font-weight: 600; }
        .hic-attach-list { display: grid; gap: 10px; max-height: min(56vh, 570px); overflow-y: auto; padding: 2px 4px 2px 2px; }
        .hic-attach-card {
          align-items: center; background: #fff; border: 1px solid #e1e7ef; border-radius: 12px;
          display: grid; gap: 18px; grid-template-columns: minmax(0, 1fr) auto; padding: 16px 18px;
          transition: border-color .16s ease, box-shadow .16s ease, transform .16s ease;
        }
        .hic-attach-card:hover { border-color: #b8c9f5; box-shadow: 0 8px 24px rgba(27, 52, 107, .08); transform: translateY(-1px); }
        .hic-attach-card-main { align-items: center; display: flex; gap: 14px; min-width: 0; }
        .hic-attach-avatar { flex: 0 0 auto; }
        .hic-attach-avatar .hic-subcontractor-avatar { border-radius: 11px; height: 50px; padding: 6px; width: 50px; }
        .hic-attach-avatar .hic-subcontractor-avatar img { border-radius: 7px; }
        .hic-attach-avatar .hic-subcontractor-role-mark { bottom: -4px; font-size: 7px; height: 19px; right: -4px; width: 19px; }
        .hic-attach-card-copy { min-width: 0; }
        .hic-attach-card-heading { align-items: center; display: flex; flex-wrap: wrap; gap: 8px; }
        .hic-attach-card-title { color: #182230; font-size: 15px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hic-attach-card-id { color: #98a2b3; font-size: 11px; font-weight: 700; letter-spacing: .03em; }
        .hic-attach-status { border-radius: 999px; font-size: 11px; font-weight: 700; padding: 5px 9px; }
        .hic-attach-party { color: #344054; font-size: 13px; margin-top: 7px; }
        .hic-attach-party strong { color: #182230; font-weight: 700; }
        .hic-attach-party-email { color: #667085; margin-left: 6px; }
        .hic-attach-details { display: grid; gap: 10px 18px; grid-template-columns: minmax(180px, 1.4fr) minmax(120px, .7fr) minmax(150px, .8fr); margin-top: 12px; }
        .hic-attach-detail { min-width: 0; }
        .hic-attach-detail-label { color: #98a2b3; display: block; font-size: 10px; font-weight: 700; letter-spacing: .05em; margin-bottom: 2px; text-transform: uppercase; }
        .hic-attach-detail-value { align-items: center; color: #475467; display: flex; font-size: 12px; gap: 5px; min-width: 0; }
        .hic-attach-detail-value span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hic-attach-card-action { align-items: flex-end; display: flex; flex-direction: column; gap: 8px; }
        .hic-attach-action { border-radius: 9px; font-weight: 700; min-width: 164px; padding: 9px 16px; }
        .hic-attach-connect-note { color: #667085; font-size: 10px; line-height: 1.35; max-width: 176px; text-align: right; }
        .hic-attach-connect-note.sync { color: #087443; }
        .hic-attach-empty { background: #fff; border: 1px dashed #cfd8e6; border-radius: 12px; color: #667085; padding: 42px 20px; text-align: center; }
        .hic-attach-empty svg { color: #98a2b3; margin-bottom: 10px; }
        .hic-attach-modal .modal-footer { background: #fff; border-top: 1px solid #e8edf5; padding: 14px 28px; }
        @media (max-width: 767.98px) {
          .hic-attach-modal .modal-header, .hic-attach-modal .modal-body { padding-left: 18px; padding-right: 18px; }
          .hic-attach-toolbar { align-items: stretch; flex-direction: column; }
          .hic-attach-search { max-width: none; }
          .hic-attach-card { align-items: stretch; grid-template-columns: 1fr; }
          .hic-attach-card-main { align-items: flex-start; }
          .hic-attach-details { grid-template-columns: 1fr; }
          .hic-attach-card-action { align-items: stretch; }
          .hic-attach-action { width: 100%; }
          .hic-attach-connect-note { max-width: none; text-align: left; }
        }
      `}</style>

      <Modal
        show={showAttach}
        onHide={() => setShowAttach(false)}
        size="xl"
        centered
        contentClassName="hic-attach-modal"
        aria-labelledby="hic-attach-modal-title"
      >
        <Modal.Header closeButton>
          <div className="hic-attach-title-row">
            <span className="hic-attach-title-icon"><Link2 size={22} /></span>
            <div>
              <Modal.Title id="hic-attach-modal-title">Connect an Existing Agreement</Modal.Title>
              <p className="hic-attach-subtitle">
                Select an unconnected subcontract agreement. Draft project details will be updated to match this homeowner contract.
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {attachLoading ? (
            <div className="hic-attach-empty"><Spinner size="sm" className="me-2" /> Loading available agreements...</div>
          ) : attachable.length === 0 ? (
            <div className="hic-attach-empty">
              <FileText size={34} />
              <div className="fw-semibold text-dark">No agreements available to connect</div>
              <div className="small mt-1">Create a new subcontract agreement or detach an eligible draft from another workflow.</div>
            </div>
          ) : (
            <>
              <div className="hic-attach-toolbar">
                <div className="hic-attach-search">
                  <Search size={18} />
                  <Form.Control
                    value={attachSearch}
                    onChange={(event) => setAttachSearch(event.target.value)}
                    placeholder="Search by agreement, subcontractor, address, or status"
                    aria-label="Search available subcontract agreements"
                  />
                </div>
                <div className="hic-attach-count">
                  {filteredAttachable.length} of {attachable.length} agreement{attachable.length === 1 ? "" : "s"}
                </div>
              </div>

              {filteredAttachable.length === 0 ? (
                <div className="hic-attach-empty">
                  <Search size={32} />
                  <div className="fw-semibold text-dark">No matching agreements</div>
                  <div className="small mt-1">Try a different title, email, address, or status.</div>
                </div>
              ) : (
                <div className="hic-attach-list" role="list">
                  {filteredAttachable.map((agreement) => {
                    const updatedAt = formatDate(agreement.updatedAt);
                    const amount = formatMoney(agreement.contractSum);
                    const subcontractor = getSubcontractorDisplay(agreement);
                    const isDraft = agreement.status === "draft";
                    return (
                      <div key={agreement.id} className="hic-attach-card" role="listitem">
                        <div className="hic-attach-card-main">
                          <div className="hic-attach-avatar">
                            <SubcontractorAvatar agreement={agreement} name={subcontractor.name} />
                          </div>
                          <div className="hic-attach-card-copy">
                            <div className="hic-attach-card-heading">
                              <div className="hic-attach-card-title" title={agreement.title || agreement.projectName}>
                                {agreement.title || agreement.projectName || `Agreement #${agreement.id}`}
                              </div>
                              <span className="hic-attach-card-id">#{agreement.id}</span>
                              <Badge
                                className={`hic-attach-status ${statusVariant(agreement.status) === "warning" ? "text-dark" : ""}`}
                                bg={statusVariant(agreement.status)}
                              >
                                {statusLabel(agreement.status)}
                              </Badge>
                            </div>
                            <div className="hic-attach-party">
                              <strong>{subcontractor.name}</strong>
                              <span className="hic-attach-party-email">{subcontractor.email}</span>
                            </div>
                            <div className="hic-attach-details">
                              <div className="hic-attach-detail">
                                <span className="hic-attach-detail-label">Project address</span>
                                <div className="hic-attach-detail-value" title={agreement.projectSite || "Project address pending"}>
                                  <MapPin size={13} /><span>{agreement.projectSite || "Not provided"}</span>
                                </div>
                              </div>
                              <div className="hic-attach-detail">
                                <span className="hic-attach-detail-label">Contract value</span>
                                <div className="hic-attach-detail-value"><span>{amount || "Not provided"}</span></div>
                              </div>
                              <div className="hic-attach-detail">
                                <span className="hic-attach-detail-label">Last updated</span>
                                <div className="hic-attach-detail-value"><CalendarDays size={13} /><span>{updatedAt || "Not available"}</span></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="hic-attach-card-action">
                          <Button
                            className="hic-attach-action"
                            variant={isDraft ? "primary" : "outline-primary"}
                            disabled={actionId !== null}
                            onClick={() => attachAgreement(agreement.id)}
                          >
                            {actionId === agreement.id ? <Spinner size="sm" /> : <><Link2 size={15} className="me-2" /> Connect to this HIC</>}
                          </Button>
                          <div className={`hic-attach-connect-note ${isDraft ? "sync" : ""}`}>
                            {isDraft
                              ? "Project and homeowner details will sync automatically."
                              : "Project address and homeowner must match this HIC."}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={openAttachModal} disabled={attachLoading}>
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
          <Button variant="light" onClick={() => setShowAttach(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      <ContractLoadingModal
        title={`Review & Sign — ${signAgreement?.title || signAgreement?.projectName || "Subcontract Agreement"}`}
        open={Boolean(signAgreement)}
        pdfURL={signAgreement?.contractUrl}
        project={null}
        sigCanvasRef={signatureRef}
        submitSignature={submitAgreementSignature}
        submitting={signing}
        submitLabel="Sign Agreement"
        submittingLabel="Signing agreement..."
        onClose={() => {
          if (!signing) setSignAgreement(null);
        }}
      />
    </div>
  );
};

export default HicSubcontractAgreements;
